import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import Constants from '@tcp/core/src/components/common/molecules/Recommendations/container/Recommendations.constants';
import { isTier1Device } from '@tcp/web/src/utils/device-tiering';
import Image from '@tcp/core/src/components/common/atoms/Image';
import { getIconPath } from '@tcp/core/src/utils';
import { showOOSproduct } from '@tcp/core/src/components/features/browse/OutfitDetails/util.js';
import {
  getLabelFromCopyOrFromTranslation,
  getDynamicLabelValues,
  renderLabel,
} from '@tcp/core/src/utils/labels/labelUtilty';
import { PDP_SUB_LABELS, pdpSubLabels } from '@tcp/core/src/utils/labels/browse/PDP.labels';
import { OUTFIT_LABELS, outfitLabels } from '@tcp/core/src/utils/labels/browse/Outfit.labels';
import {
  StyledBreadcrumbsContainer,
  StyledItemsInThisLookLabel,
  StyledOutfitCarouselContainer,
  StyledOutfitContainer,
  StyledOutfitList,
  StyledOutfitProductContainer,
  StyledPager,
  StyledProductTileSkeleton,
  StyledRecommendationsContainer,
  StyledOufitListWrapper,
  StyledItemsUnavailableWrapper,
  StyledItemsUnavailableText,
} from '../styles/OutfitDetailsNew.style';
import { BundleProductItemVanilla } from '../../BundleProduct/molecules/BundleProductsGrid/views/BundleProductItem.view';
import OutfitBreadcrumbs from '../molecules/OutfitBreadcrumbs/OutfitBreadcrumbs';
import OutfitCarousel from '../molecules/OutfitCarousel/OutfitCarousel';
import { recommendationStyles } from '../OutfitDetails.style';

const Recommendations = dynamic(() =>
  import(
    // eslint-disable-next-line extra-rules/no-commented-out-code
    /* webpackChunkName: "recommendations" */ '@tcp/core/src/components/common/molecules/Recommendations'
  )
);

function findIndex(currentOutfit, selectedProductsList) {
  const { outfitId } = currentOutfit;
  for (let i = 0; i < selectedProductsList?.length; i += 1) {
    // eslint-disable-next-line eqeqeq
    if (selectedProductsList[i]?.id == outfitId) {
      return i;
    }
  }
  return -1;
}

const PAGE_NAME = Constants.RECOMMENDATIONS_PAGES_MAPPING.OUTFIT;
const willAlsoViewedCarouselDisplay = isTier1Device();

const OutfitDetailsNew = ({
  currentOutfit,
  inYourBagProducts,
  getInYourBagData,
  setIsOutfitPage,
  styliticsProductTabList,
  getOutfit,
  outfitProducts,
  onQuickViewOpenClick,
  selectedOutfitId,
  isLoading,
  isDynamicBadgeEnabled,
  promotionsData,
  isUpdatedPromotionsDisplayEnabled,
  isBuyMorePromoEnabled,
  serverTime,
  isKeepAliveEnabled,
  unavailableCount,
}) => {
  const dynamicBrowseLabels = getDynamicLabelValues('dynamic.browse_d');

  const youMayAlsoLike = renderLabel(
    dynamicBrowseLabels.lbl_you_may_also_like_outfit,
    'lbl_you_may_also_like_outfit'
  );
  const recentlyViewed = renderLabel(
    dynamicBrowseLabels.lbl_recently_viewed_outfit,
    'lbl_recently_viewed_outfit'
  );

  const pdpLabels = getLabelFromCopyOrFromTranslation(PDP_SUB_LABELS, pdpSubLabels);

  const lookXofY = renderLabel(pdpLabels.lbl_look_x_of_y, 'lbl_look_x_of_y');
  const itemsInThisLook = renderLabel(pdpLabels.lbl_items_in_this_look, 'lbl_items_in_this_look');

  const outfitUnavailableLabel = getLabelFromCopyOrFromTranslation(
    `${OUTFIT_LABELS}.lbl_outfit_unavailable`,
    outfitLabels
  );
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const recommendationAttributes = {
    variations: 'moduleO',
    page: PAGE_NAME,
    partNumber: `${currentOutfit.outfitId}`,
    showLoyaltyPromotionMessage: false,
    headerAlignment: 'left',
    priceOnly: true,
    recommendationCarouselRedesign: true,
  };

  const selectedOutfit = useMemo(
    () => findIndex(currentOutfit, styliticsProductTabList[selectedOutfitId]),
    [selectedOutfitId]
  );
  const selectedProductsLists = useMemo(() => {
    const styliticsProductList = styliticsProductTabList[selectedOutfitId];
    if (!styliticsProductList?.length) {
      return [currentOutfit].map((outfit) => ({
        imageUrl: outfit.outfitImageUrl,
        id: outfit.outfitId,
        ...outfit,
      }));
    }
    return [
      ...styliticsProductList.slice(selectedOutfit),
      ...styliticsProductList.slice(0, selectedOutfit),
    ];
  }, [styliticsProductTabList, selectedOutfitId]);

  useEffect(() => {
    setIsOutfitPage(true);
    return () => setIsOutfitPage(false);
  }, []);

  useEffect(() => {
    getInYourBagData({ debounce: 500 });
  }, [currentImageIndex]);

  const LookXofY = lookXofY
    .replace(/\{x\}/, currentImageIndex + 1)
    .replace(/\{y\}/, selectedProductsLists.length);

  return (
    <StyledOutfitContainer>
      <StyledBreadcrumbsContainer>
        <OutfitBreadcrumbs selectedProductId={selectedOutfitId} />
      </StyledBreadcrumbsContainer>
      {selectedProductsLists.length > 1 ? (
        <StyledPager fontFamily="secondary" fontWeight="regular" fontSize="fs16">
          {LookXofY}
        </StyledPager>
      ) : null}
      <StyledOutfitProductContainer>
        <StyledOutfitCarouselContainer>
          <OutfitCarousel
            getOutfit={getOutfit}
            currentImageIndex={currentImageIndex}
            setCurrentImageIndex={setCurrentImageIndex}
            selectedProductsLists={selectedProductsLists}
            currentOutfitProducts={selectedProductsLists[currentImageIndex]}
          />
        </StyledOutfitCarouselContainer>
        <StyledItemsInThisLookLabel fontFamily="secondary" fontWeight="bold" fontSize="fs14">
          {itemsInThisLook}
        </StyledItemsInThisLookLabel>
        <StyledOufitListWrapper>
          <StyledOutfitList className="products-list-grid">
            {outfitProducts?.map((product) => {
              return (
                showOOSproduct(product, isKeepAliveEnabled) && (
                  <li key={`${product.productId}`} className="product-list-item">
                    {isLoading ? (
                      <StyledProductTileSkeleton />
                    ) : (
                      <BundleProductItemVanilla
                        productItem={product}
                        inYourBagProducts={inYourBagProducts}
                        onQuickViewOpenClick={onQuickViewOpenClick}
                        isOutfitPage
                        isDynamicBadgeEnabled={isDynamicBadgeEnabled}
                        promotionsData={promotionsData}
                        isUpdatedPromotionsDisplayEnabled={isUpdatedPromotionsDisplayEnabled}
                        isBuyMorePromoEnabled={isBuyMorePromoEnabled}
                        serverTime={serverTime}
                        isKeepAliveEnabled={isKeepAliveEnabled}
                      />
                    )}
                  </li>
                )
              );
            })}
          </StyledOutfitList>
          {!!unavailableCount && (
            <StyledItemsUnavailableWrapper>
              <Image src={getIconPath('error-message-icon')} width="16px" height="16px" />
              <StyledItemsUnavailableText
                fontFamily="secondary"
                fontSize={['fs12', 'fs10', 'fs14']}
              >
                {`${unavailableCount} ${outfitUnavailableLabel}`}
              </StyledItemsUnavailableText>
            </StyledItemsUnavailableWrapper>
          )}
        </StyledOufitListWrapper>
      </StyledOutfitProductContainer>
      <StyledRecommendationsContainer>
        <Recommendations
          inheritedStyles={recommendationStyles}
          headerLabel={youMayAlsoLike}
          page={PAGE_NAME}
          {...recommendationAttributes}
          sequence="1"
          isOutFitPage
        />
        {willAlsoViewedCarouselDisplay && (
          <Recommendations
            inheritedStyles={recommendationStyles}
            headerLabel={recentlyViewed}
            portalValue={Constants.RECOMMENDATIONS_MBOXNAMES.RECENTLY_VIEWED}
            page={PAGE_NAME}
            {...recommendationAttributes}
            sequence="2"
            isOutFitPage
          />
        )}
      </StyledRecommendationsContainer>
    </StyledOutfitContainer>
  );
};

export default OutfitDetailsNew;

OutfitDetailsNew.propTypes = {
  currentOutfit: PropTypes.shape({
    outfitId: PropTypes.string,
  }),
  inYourBagProducts: PropTypes.shape([]),
  getInYourBagData: PropTypes.func,
  setIsOutfitPage: PropTypes.func,
  styliticsProductTabList: PropTypes.shape([]),
  router: PropTypes.shape({}),
  getOutfit: PropTypes.func,
  outfitProducts: PropTypes.shape([]),
  onQuickViewOpenClick: PropTypes.func,
  selectedOutfitId: PropTypes.string,
  isLoading: PropTypes.bool,
  isDynamicBadgeEnabled: PropTypes.bool,
  isKeepAliveEnabled: PropTypes.bool,
};

OutfitDetailsNew.defaultProps = {
  currentOutfit: {},
  inYourBagProducts: [],
  getInYourBagData: () => {},
  setIsOutfitPage: () => {},
  styliticsProductTabList: [],
  router: {},
  getOutfit: () => {},
  outfitProducts: [],
  onQuickViewOpenClick: () => {},
  selectedOutfitId: '',
  isLoading: false,
  isDynamicBadgeEnabled: true,
  isKeepAliveEnabled: false,
};
